import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import  Chatbot  from '../../components/ui/chatbot';
import BookingProgress from '../../components/ui/BookingProgress';
import ServiceCard from './components/ServiceCard';
import CalendarWidget from './components/CalendarWidget';
import TimeSlotGrid from './components/TimeSlotGrid';
import ClientInfoForm from './components/ClientInfoForm';
import BookingSummary from './components/BookingSummary';
import BookingConfirmation from './components/BookingConfirmation';
import Button from '../../components/ui/Button';

const ServiceBooking = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  // Mock services data
  const services = [
    {
      id: 1,
      name: "Wedding Photography",
      description: "Capture your special day with comprehensive wedding coverage including ceremony, reception, and couple portraits.",
      price: 2500,
      duration: "8-10 hours",
      popular: true,
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTc9APxkj0xClmrU3PpMZglHQkx446nQPG6lA&s",
      features: [
        "Full day coverage",
        "2 photographers",
        "500+ edited photos",
        "Online gallery",
        "Print release included",
        "Engagement session"
      ]
    },
    {
      id: 2,
      name: "Portrait Session",
      description: "Professional portrait photography for individuals, families, or couples in studio or outdoor locations.",
      price: 350,
      duration: "1-2 hours",
      popular: false,
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTc9APxkj0xClmrU3PpMZglHQkx446nQPG6lA&s",
      features: [
        "1-2 hour session",
        "Multiple outfit changes",
        "50+ edited photos",
        "Online gallery",
        "Print release",
        "Location flexibility"
      ]
    },
    {
      id: 3,
      name: "Event Photography",
      description: "Professional coverage for corporate events, parties, and special occasions with candid and posed shots.",
      price: 800,
      duration: "4-6 hours",
      popular: false,
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTc9APxkj0xClmrU3PpMZglHQkx446nQPG6lA&s",
      features: [
        "Event coverage",
        "Candid photography",
        "Group photos",
        "200+ edited photos",
        "Same-day highlights",
        "Online delivery"
      ]
    },
    {
      id: 4,
      name: "Commercial Photography",
      description: "Professional product, headshot, and business photography for marketing and promotional materials.",
      price: 1200,
      duration: "3-4 hours",
      popular: false,
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTc9APxkj0xClmrU3PpMZglHQkx446nQPG6lA&s",
      features: [
        "Product photography",
        "Professional headshots",
        "Brand photography",
        "Commercial license",
        "High-res files",
        "Rush delivery available"
      ]
    }
  ];

  const steps = [
    { id: 1, title: 'Select Service', description: 'Choose your photography service' },
    { id: 2, title: 'Pick Date', description: 'Select your preferred date' },
    { id: 3, title: 'Choose Time', description: 'Pick available time slot' },
    { id: 4, title: 'Your Details', description: 'Provide contact information' }
  ];

  const handleServiceSelect = (service) => setSelectedService(service);
  const handleDateSelect = (date) => setSelectedDate(date);
  const handleTimeSelect = (time) => setSelectedTime(time);
  const handleFormChange = (data) => setFormData(data);

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (step) => setCurrentStep(step);

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1: return selectedService !== null;
      case 2: return selectedDate !== null;
      case 3: return selectedTime !== null;
      case 4: return formData?.firstName && formData?.email && formData?.phone && formData?.location;
      default: return false;
    }
  };

  const handleNewBooking = () => {
    setCurrentStep(1);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({});
    setBookingComplete(false);
    setBookingData(null);
  };

  const handleGoHome = () => navigate('/');

  if (bookingComplete && bookingData) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <BookingConfirmation
              bookingData={bookingData}
              onNewBooking={handleNewBooking}
              onGoHome={handleGoHome}
            />
          </div>
        </main>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services?.map((service) => (
              <ServiceCard
                key={service?.id}
                service={service}
                isSelected={selectedService?.id === service?.id}
                onSelect={handleServiceSelect}
              />
            ))}
          </div>
        );
      case 2:
        return (
          <div className="max-w-2xl mx-auto">
            <CalendarWidget
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
            />
          </div>
        );
      case 3:
        return (
          <div className="max-w-2xl mx-auto">
            <TimeSlotGrid
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              selectedDate={selectedDate}
            />
          </div>
        );
      case 4:
        return (
          <div className="max-w-2xl mx-auto">
            <ClientInfoForm
              formData={formData}
              onFormChange={handleFormChange}
              isSubmitting={isSubmitting}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-4">
              Book Your Photography Session
            </h1>
            <p className="text-muted-foreground font-body max-w-2xl mx-auto">
              Schedule your perfect photography session in just a few simple steps. 
              Choose your service, pick a date, and we'll take care of the rest.
            </p>
          </div>

          {/* Progress Indicator */}
          <BookingProgress
            currentStep={currentStep}
            totalSteps={4}
            steps={steps}
            onStepClick={handleStepClick}
          />

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Step Content */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <h2 className="text-2xl font-heading font-semibold text-primary mb-2">
                  {steps?.[currentStep - 1]?.title}
                </h2>
                <p className="text-muted-foreground font-body">
                  {steps?.[currentStep - 1]?.description}
                </p>
              </div>

              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>

                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <span>Step {currentStep} of {steps?.length}</span>
                </div>

{currentStep < 4 ? (
  <Button
    variant="default"
    onClick={handleNext}
    disabled={!canProceedToNext()}
    iconName="ChevronRight"
    iconPosition="right"
  >
    Next
  </Button>
) : null}
              </div>
            </div>

            {/* Booking Summary */}
            <div className="lg:col-span-1">
              <BookingSummary
                selectedService={selectedService}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                formData={formData}
                isSticky={true}
                setBookingComplete={setBookingComplete}
                setBookingData={setBookingData}
                setIsSubmitting={setIsSubmitting}
              />
            </div>
          </div>
        </div>
      </main>
      <Chatbot />
    </div>
  );
};

export default ServiceBooking;
